import { MouseEvent, useCallback, useEffect, useRef } from 'react';

export const useRawTableCrosshairHover = (resetKey: string) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const hoveredElementsRef = useRef<HTMLElement[]>([]);
  const hoveredColRef = useRef<number | null>(null);
  const hoveredRowRef = useRef<number | null>(null);

  const clearHover = useCallback(() => {
    hoveredElementsRef.current.forEach((el) => {
      el.classList.remove('rt-col-hover', 'rt-row-hover');
    });
    hoveredElementsRef.current = [];
    hoveredColRef.current = null;
    hoveredRowRef.current = null;
  }, []);

  useEffect(() => {
    clearHover();
  }, [clearHover, resetKey]);

  const handleMouseMove = useCallback(
    (event: MouseEvent<HTMLTableElement>) => {
      const cell = (event.target as HTMLElement).closest('th, td');
      if (!cell || !tableRef.current?.contains(cell)) {
        clearHover();
        return;
      }

      const colAttr = cell.getAttribute('data-col-index');
      const rowAttr = cell.getAttribute('data-row-index');
      const colIndex = colAttr !== null ? parseInt(colAttr, 10) : null;
      const rowIndex = rowAttr !== null ? parseInt(rowAttr, 10) : null;

      if (hoveredColRef.current === colIndex && hoveredRowRef.current === rowIndex) {
        return;
      }

      clearHover();

      const table = tableRef.current;
      if (!table) {
        return;
      }

      hoveredColRef.current = colIndex;
      hoveredRowRef.current = rowIndex;

      const touched: HTMLElement[] = [];

      if (colIndex !== null) {
        table.querySelectorAll<HTMLElement>(`[data-col-index="${colIndex}"]`).forEach((el) => {
          el.classList.add('rt-col-hover');
          touched.push(el);
        });
      }

      if (rowIndex !== null) {
        table.querySelectorAll<HTMLElement>(`[data-row-index="${rowIndex}"]`).forEach((el) => {
          el.classList.add('rt-row-hover');
          touched.push(el);
        });
      }

      hoveredElementsRef.current = touched;
    },
    [clearHover]
  );

  return { tableRef, clearHover, handleMouseMove };
};
